import React, { useEffect } from "react";
import { Form, Input, Checkbox, Modal } from "semantic-ui-react";
import { FlexRow } from "../../components";
import { AppButton, AppPrimaryButton } from "elements";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PermissionSettingService } from "utility/services/permission";

const permissionSchema = yup.object().shape({
  PermissionName: yup.string().required("Permission Name is required"),
  Description: yup.string().required("Description is required"),
  Category: yup.string().required("Category is required"),
  IsActive: yup.boolean(),
});

const initialPermissionState = {
  PermissionKey: "",
  PermissionName: "",
  Description: "",
  Category: "",
  IsActive: true,
};

const PermissionModal = ({
  open,
  onClose,
  editItem,
  refresh,
}) => {
  const isEditing = !!editItem;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialPermissionState,
    resolver: yupResolver(permissionSchema),
  });

  const watchPermissionName = watch("PermissionName");
  const watchCategory = watch("Category");

  useEffect(() => {
    if (watchPermissionName) {
      const cleanPermissionName = watchPermissionName.replace(/[/%]/g, "");
      const parts = cleanPermissionName.split(" - ");
      const formattedParts = parts
        .map((part) => {
          const words = part.trim().split(/\s+/).filter(Boolean);
          return words
            .map((word, index) => {
              const lowerWord = word.toLowerCase();
              if (index === 0) {
                return lowerWord;
              }
              return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
            })
            .join("");
        })
        .filter(Boolean);

      const formatted = formattedParts.join(".");
      const prefix = watchCategory ? watchCategory.trim().toLowerCase() : "menu";
      setValue("PermissionKey", `${prefix}.${formatted}`);
    } else {
      setValue("PermissionKey", "");
    }
  }, [watchPermissionName, watchCategory, setValue]);

  useEffect(() => {
    if (editItem) {
      reset(editItem);
    } else {
      reset(initialPermissionState);
    }
  }, [editItem]);

  const onSubmit = async (formData) => {
    try {
      if (isEditing) {
        await PermissionSettingService.update(
          editItem.PermissionId,
          formData,
        );
      } else {
        await PermissionSettingService.create(formData);
      }

      refresh();
      onClose();
      reset(initialPermissionState);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>
        {isEditing ? "Edit Permission" : "Add Permission"}
      </Modal.Header>

      <Modal.Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group widths="equal">
            <Form.Field error={!!errors.PermissionName}>
              <label>Permission Name</label>

              <Controller
                name="PermissionName"
                control={control}
                render={({ field }) => (
                  <Input placeholder="Permission Name" {...field} />
                )}
              />

              {errors.PermissionName && (
                <span className="text-red-500">
                  {errors.PermissionName.message}
                </span>
              )}
            </Form.Field>

            <Form.Field>
              <label>Permission Key</label>

              <Controller
                name="PermissionKey"
                control={control}
                render={({ field }) => (
                  <Input {...field} readOnly />
                )}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field error={!!errors.Category}>
              <label>Category</label>

              <Controller
                name="Category"
                control={control}
                render={({ field }) => (
                  <Input placeholder="Category" {...field} />
                )}
              />

              {errors.Category && (
                <span className="text-red-500">
                  {errors.Category.message}
                </span>
              )}
            </Form.Field>

            <Form.Field>
              <label>Description</label>

              <Controller
                name="Description"
                control={control}
                render={({ field }) => (
                  <Input placeholder="Description" {...field} />
                )}
              />
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>Is Active</label>

            <Controller
              name="IsActive"
              control={control}
              render={({ field }) => (
                <Checkbox
                  toggle
                  checked={field.value}
                  onChange={(e, data) =>
                    field.onChange(data.checked)
                  }
                />
              )}
            />
          </Form.Field>

          <FlexRow className="justify-content-end">
            <AppPrimaryButton type="submit">
              {isEditing ? "Update Permission" : "Save Permission"}
            </AppPrimaryButton>

            <AppButton type="button" onClick={onClose}>
              Cancel
            </AppButton>
          </FlexRow>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

export default PermissionModal;